import ApiPricingVariables from "api/ApiPricingVariables"
import { ChangeElement, List, useHandleObjList } from "hooks/useHandleObjList"
import { useAppSelector } from "reducers/store"
import { isValidPricingItem } from "pages/purchase-plans/validators/pricingVariables"
import {
  PricingTypes,
  initialPricingCalculator,
  initialPricingExpression
} from "pages/purchase-plans/constants/defaultValues"

import { useCallback, useEffect, useMemo, useState } from "react"
import { useMutation } from "react-query"
import useDeepCompareEffect from "use-deep-compare-effect"

export interface IPricingVariables {
  list: List<App.Pricings>
  mathExpression: string
  isMathExpressionParsed: boolean
  changeElement: ChangeElement<App.Pricings>
  handleMathExpressionChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSubmitMathExpression: () => void
  loading: boolean
}

const initialPricing = {
  calculator: initialPricingCalculator,
  expression: initialPricingExpression
}

const usePricingVariables = (pricingType: PricingTypes, initialData?: App.PricingsData<App.Pricings[]>) => {
  const [mathExpression, setMathExpression] = useState<string>("")

  const [isMathExpressionParsed, setIsMathExpressionParsed] = useState<boolean>(false)

  const { purchasePlanId, productId } = useAppSelector((state) => state.purchasePlan.identifiers)

  const { list, changeElement, replaceList } = useHandleObjList<App.Pricings>(initialPricing[pricingType])

  useDeepCompareEffect(() => {
    if (!initialData?.list) return

    replaceList(initialData.list)

    if (initialData?.list.length > 0) {
      setIsMathExpressionParsed(true)
    }
  }, [initialData?.list])

  useEffect(() => {
    const initialMathExp = initialData?.mathExp || ""

    setMathExpression(initialMathExp)
  }, [initialData?.mathExp])

  const handleMathExpressionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMathExpression(e.target.value)
  }, [])

  const resetData = useCallback((data?: App.PricingsData<App.Pricings[]>) => {
    replaceList(data?.list || [])
    setMathExpression(data?.mathExp || "")
  }, [])

  const _parsePricing = useMutation({
    mutationFn: async (data: App.StrExpressionPayload) => {
      return new ApiPricingVariables().parsePricing(data)
    },
    onSuccess: (data) => {
      const { variables } = data.parsedExpression

      replaceList(variables)

      setIsMathExpressionParsed(true)
    }
  })

  const onSubmitMathExpression = useCallback(() => {
    _parsePricing.mutate({
      strExpression: mathExpression,
      productId,
      purchasePlanId,
      pricingType
    })
  }, [mathExpression, productId, purchasePlanId, pricingType])

  const isListValid = useMemo(() => {
    const isListEmpty = list.length === 0

    const areListItemsAllValid = list.every(isValidPricingItem(pricingType))

    return !isListEmpty && areListItemsAllValid
  }, [list, pricingType, isValidPricingItem])

  const isMathExpValid = useMemo(() => {
    const isMathExpressionEmpty = mathExpression === ""

    return !isMathExpressionEmpty
  }, [mathExpression])

  return {
    props: {
      list,
      mathExpression,
      isMathExpressionParsed,
      changeElement,
      handleMathExpressionChange,
      onSubmitMathExpression,
      loading: _parsePricing.isLoading
    },
    isValid: isListValid && isMathExpValid,
    resetData
  }
}

export default usePricingVariables
